import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';

type ICaseTransactionSnapshot = {
  entityType: string;
  from: string;
  to: string;
  currency: string;
  showExpectedBehaviour: boolean;
  window: number;
};

const CaseTransactionSnapshotAuditDetails = () => {
  const record = useRecordContext<AuditTrack<ICaseTransactionSnapshot>>();
  const { data, isLoading } = useGetOne<AuditTrack<ICaseTransactionSnapshot>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<ICaseTransactionSnapshot>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    entityType: resourceData.entityType?.hasChanged,
    from: resourceData.from?.hasChanged,
    to: resourceData.to?.hasChanged,
    currency: resourceData.currency?.hasChanged,
    showExpectedBehaviour: resourceData.showExpectedBehaviour?.hasChanged,
    window: resourceData.window?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.entityType && <TextDiffField source="entityType" label="Entity type" />}
          {hasFieldChanges.from && <TextDiffField source="from" label="From" />}
          {hasFieldChanges.to && <TextDiffField source="to" label="To" />}
          {hasFieldChanges.currency && <TextDiffField source="currency" label="Currency" />}
          {hasFieldChanges.showExpectedBehaviour && (
            <TextDiffField source="showExpectedBehaviour" label="Show expected behaviour" />
          )}
          {hasFieldChanges.window && <TextDiffField source="window" label="Window" />}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default CaseTransactionSnapshotAuditDetails;
